import React, { useState, useEffect } from 'react';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { getProjectState, getTempState } from '@starflow/app/store';
import { updatePartialState, useCategories, usePartialState } from '../../store/state';
import { queryCategories, queryModelState, requestSuggestedKeywords } from '../../store/actions';
import { Filter } from '../base/Filter';
import { BestModelList } from '../base/BestModelList';
import { ModelList } from '../base/List';

const sortOptions = [
  { value: '0', label: translate('common_sort1') },
  { value: '2', label: translate('common_sort3') },
  { value: '1', label: translate('common_sort2') },
];

const cardSize = { width: 185, height: 272 };

export const AllCheckpoints = React.memo(() => {
  const [filterKey, setFilterKey] = useState(0);
  const { filters, data } = usePartialState('checkpoint.all');
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const categories = useCategories((state) => state.checkpoint);
  const handleApplyFilter = (value) => {
    // 筛选项变更，需要重置分页和现有内容
    updatePartialState('checkpoint.all.data', { items: [], total: 0, hasMore: true });
    updatePartialState('checkpoint.all.filters', { page: 1, ...value });
    queryModelState('checkpoint', 'all');
  };

  const handleCancelSearch = (event) => {
    handleApplyFilter({ keyword: '' });
    setFilterKey(Date.now());
    event.stopPropagation();
  };

  const handleScroll = (event) => {
    if (!data.hasMore || data.loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      updatePartialState('checkpoint.all.filters', { page: filters.page + 1 });
      queryModelState('checkpoint', 'all');
    }
  };

  useEffect(() => {
    // 获取当前生成器的基础模型
    const modelPickingUnitId = getTempState('modelPickingUnitId');
    const unitData = getProjectState('nodeMap')[modelPickingUnitId]?.data;

    if (unitData?.checkpoint) {
      requestSuggestedKeywords([unitData.checkpoint.modelUuid]).then(setSuggestedKeywords);
    }

    queryCategories('checkpoint');
    queryModelState('checkpoint', 'all', {}, true);
  }, []);

  return (
    <main className="flex items-stretch flex-1 overflow-hidden">
      <aside className="flex flex-col items-stretch w-[150px] p-2 space-y-1 border-r border-[#efefef]">
        {filters.keyword ? (
          <div className="flex items-center">
            <button onClick={handleCancelSearch} className="flex items-center w-4 h-4 rotate-180">
              <Icon className="icon-pro_arrow_right" fontSize={14} color="#7F7F7F" />
            </button>
            <span className="ml-2 translate-y-px text-[13px] font-normal text-[#2D2D2D]">
              {translate('model_search')}
              {data.loading ? null : (
                <span className="text-[8C8C8C]">
                  ({data.total >= 1000 ? `${parseInt(data.total / 1000)}k+` : data.total})
                </span>
              )}
            </span>
          </div>
        ) : (
          categories.catalogArr.map((item) => (
            <button
              key={item.code}
              disabled={filters.category === item.code}
              onClick={() => handleApplyFilter({ category: item.code })}
              className="flex items-center py-[8px] px-3 rounded text-[13px] whitespace-normal break-words text-left leading-[18px] overflow-hidden text-[#7f7f7f] hover:bg-[#F4F6F8] disabled:bg-primary/15 disabled:text-[#2d2d2d] disabled:font-semibold"
            >
              {item.tagLabel}
            </button>
          ))
        )}
      </aside>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="relative z-[5] px-5 py-2" style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.05)' }}>
          <Filter
            key={filterKey}
            value={filters}
            searchWidth={400}
            onSubmit={handleApplyFilter}
            sortOptions={sortOptions}
            suggestedKeywords={suggestedKeywords}
          />
        </div>
        <div onScroll={handleScroll} className="flex-1 px-5 overflow-auto tiny-scrollbar bg-[#f9fbfb]">
          {filters.sortType === '0' ? (
            <BestModelList
              data={data}
              cardSize={cardSize}
              showTag={!filters.keyword}
              query={filters.keyword}
              cols={4}
              withHotBadge={null}
              withAuthorInfo
            />
          ) : (
            <ModelList
              data={data}
              cardSize={cardSize}
              cols={4}
              showTag={!filters.keyword}
              withHotBadge={filters.sortType === '2' && !filters.keyword}
              withAuthorInfo
            />
          )}
        </div>
      </div>
    </main>
  );
});
